<template>
    <div>
        <b-container class="py-3">
            <b-row>
                <b-col>

                    <page-title :title="pageTitle" class="text-center" />
                    <hr />

                    <b-row>
                        <b-col>
                            <steps :steps="steps" :stepIndex="step + stepsOffset" @changeStep="onChangeStep"></steps>
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col md="2"></b-col>
                        <b-col md="8">
                            <div v-show="step == 0">
                                <auth-form page="create" @authenticated="onAuthenticated" signUpHeader="Create an Account" />
                            </div>

                            <div v-show="step == 1">
                                <div class="text-center">
                                    <h2>Choose a Subscription</h2>
                                </div>

                                <div class="text-center">
                                    <b-card-group deck class="mb-3 text-center">
                                        <b-card class="mb-4 shadow-sm">
                                            <template v-slot:header>
                                                <h4 class="my-0">Monthly Subscription</h4>
                                            </template>
                                            <b-card-body>
                                                <b-card-title>
                                                    <h1>$39.80 <small class="text-muted">/ month</small></h1>
                                                </b-card-title>
                                                <p>The monthly subscription has no long-term commitment and can be canceled at any time.</p>
                                            </b-card-body>
                                            <template v-slot:footer>
                                                <b-button variant="secondary" @click="chooseSubscription('StandardMonthly')">Buy Monthly Subscription</b-button>
                                            </template>
                                        </b-card>
                                        <b-card class="mb-4 shadow-sm">
                                            <template v-slot:header>
                                                <h4 class="my-0">
                                                    Yearly Subscription
                                                    <b-badge variant="success">Best Deal</b-badge>
                                                </h4>
                                            </template>
                                            <b-card-body>
                                                <b-card-title>
                                                    <h1>$398.00 <small class="text-muted">/ year</small></h1>
                                                </b-card-title>
                                                <p>Save almost $80/year with an annual subscription.</p>
                                            </b-card-body>
                                            <template v-slot:footer>
                                                <b-button variant="primary" @click="chooseSubscription('StandardYearly')">Buy Yearly Subscription</b-button>
                                            </template>
                                        </b-card>
                                    </b-card-group>
                                </div>

                                <b-row class="mt-2">
                                    <b-col md="2" sm="1">
                                    </b-col>
                                    <b-col md="8" sm="10">
                                        <b-card>
                                            <h2>All subscriptions include:</h2>
                                            <ul class="mt-3 mb-2">
                                                <li>Unlimited Mitigation Reports surveys sent to your clients via email or text message link.</li>
                                                <li>Automatic saving and ability to resume client survey.</li>
                                                <li>Automatic reminders to clients if surveys aren't completed.</li>
                                                <li>Attorney dashboard to track each report's status.</li>
                                                <li>Reports returned in an organized and editable Microsoft Word file.</li>
                                                <li>Access to 13 validated health, AODA, and Trauma screens.</li>
                                                <li>Bank level privacy and security.</li>
                                            </ul>
                                        </b-card>
                                    </b-col>
                                </b-row>


                                <div class="mt-2 mb-4 text-center">
                                    <b>Enterprise Subscription</b><br />
                                    Serve an entire firm or Public Defender's office with an enterprise subscription. Contact us for details.
                                </div>

                            </div>

                            <div v-show="step == 2">
                                <div class="text-center">
                                    <h2>Provide Payment Details</h2>
                                    <p>Please provide the following information.</p>
                                </div>
                                <b-row>
                                    <b-col md="3"></b-col>
                                    <b-col md="6">
                                        <b-form @submit.prevent="onSubmit">
                                            <b-alert v-if="error" ref="errorAlert" show variant="danger"><span v-html="error"></span></b-alert>
                                            <text-control v-model="purchaseOptions.firstName" label="First Name" required />
                                            <text-control v-model="purchaseOptions.lastName" label="Last Name" required />
                                            <text-control v-model="purchaseOptions.line1" label="Address Line 1" required />
                                            <text-control v-model="purchaseOptions.line2" label="Address Line 2" />
                                            <text-control v-model="purchaseOptions.city" label="City" required />
                                            <select-list-control v-model="purchaseOptions.state" label="State" :options="states"></select-list-control>
                                            <text-control v-model="purchaseOptions.postalCode" label="ZIP Code" required />

                                            <b-form-group label="Credit Card" :state="cardError ? false : null" :invalid-feedback="cardError">
                                                <div class="form-control stripe-card-container" :class="{ 'is-invalid': cardError }">
                                                    <div ref="card"></div>
                                                </div>
                                            </b-form-group>

                                            <hr />

                                            <b-form-row>
                                                <b-col sm="8" class="form-static-label">
                                                    {{subscriptionName}}<br />
                                                    <small><b-link @click="step = 1">Change Subscription</b-link></small>
                                                </b-col>
                                                <b-col sm="4" class="form-static-value text-right">${{subscriptionPrice.toFixed(2)}}{{priceDuration}}</b-col>
                                            </b-form-row>

                                            <b-form-row v-if="promotionCodeValidation && promotionCodeValidation.isValid">
                                                <b-col sm="8" class="form-static-label">
                                                    Promo Code: "{{promotionCodeValidation.code.toUpperCase()}}"<br />
                                                    <small><b-link @click="showPromotionCode = !showPromotionCode">Change Promo Code</b-link></small>
                                                </b-col>
                                                <b-col sm="4" class="form-static-value text-right">-${{promotionCodeDiscount.toFixed(2)}}{{priceDuration}}</b-col>
                                            </b-form-row>

                                            <b-form-row v-if="!promotionCodeValidation || !promotionCodeValidation.isValid">
                                                <b-col>
                                                    <small><b-link @click="showPromotionCode = !showPromotionCode">Apply a Promo Code</b-link></small>
                                                </b-col>
                                            </b-form-row>

                                            <b-form-row v-if="promotionCodeValidation && !promotionCodeValidation.isValid">
                                                <b-col>
                                                    <small class="text-danger">The promo code "{{promotionCodeValidation.code}}" is not valid.</small>
                                                </b-col>
                                            </b-form-row>

                                            <b-form-row v-if="showPromotionCode">
                                                <b-col sm="8">
                                                    <b-form-input v-model="promotionCode"  :disabled="isPurchasing || isValidatingPromo" @keydown.enter.prevent="applyPromotionCode" />
                                                </b-col>
                                                <b-col sm="4" class="text-right">
                                                    <b-button :disabled="isPurchasing || isValidatingPromo" @click="applyPromotionCode">Apply</b-button>
                                                </b-col>
                                            </b-form-row>

                                            <b-form-row>
                                                <b-col sm="8" class="form-static-label">
                                                    Total
                                                </b-col>
                                                <b-col sm="4" class="form-static-value text-right">${{totalPrice.toFixed(2)}}{{priceDuration}}</b-col>
                                            </b-form-row>

                                            <hr />

                                            <div class="mt-3">
                                                <b-button type="submit" variant="primary" :disabled="isPurchasing">
                                                    <b-spinner small v-if="isPurchasing"></b-spinner>
                                                    Purchase Subscription
                                                </b-button>
                                            </div>

                                        </b-form>
                                    </b-col>
                                </b-row>
                            </div>

                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </b-container>

    </div>
</template>

<script>
// This tells ESLint that there's a global var named "Stripe", which comes from the Stripe SDK
/*global Stripe*/

import AuthMixin from '../Mixins/AuthMixin.vue';
import SubscriptionMixin from '../Mixins/SubscriptionMixin.vue';
import axios from "axios";
import Constants from '../Common/Constants.js';

const usStripeKey = 'pk_live_51H3CVrCDuMoiniEpeAsxeoBj34g1zGLwOqZ0cdFeTSmONlGab4VMSfKTAFHspYQGCUMOJanfhOUJZT3706DkU6Ka00VdXJvqNG';
let stripeKey = usStripeKey;
if (window.location.hostname.toLowerCase() != 'app.justicestory.com')
          stripeKey = 'pk_test_51H3CVrCDuMoiniEppca8otprdMcuM4Cfp8tMIwY1zcnOyrX3wybTQ5cjfX9FsltQOa3b9jXtjvP34fAp2xm6uKZY00jZIMHUzZ';
// Optional build-time override for one isolated Swiss Stripe test purchase.
if (process.env.VUE_APP_SWISS_STRIPE_LIVE_PUBLISHABLE_KEY)
          stripeKey = process.env.VUE_APP_SWISS_STRIPE_LIVE_PUBLISHABLE_KEY;

let isStripeLoaded = (typeof Stripe !== 'undefined');
if (!isStripeLoaded)
          console.log('Stripe is not loaded');

let stripe = isStripeLoaded ? Stripe(stripeKey) : null;
let elements = isStripeLoaded ? stripe.elements() : null;
let style = {
          base: {
            color: "#32325d",
            //fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontFamily: '"Spectral", Georgia, "Times New Roman", Times, serif',
            fontSmoothing: "antialiased",
            fontSize: "16px",
            "::placeholder": {
              color: "#aab7c4"
            }
          },
          invalid: {
            color: "#fa755a",
            iconColor: "#fa755a"
          }
};

let cardElement = isStripeLoaded ? elements.create("card", { style: style }) : null;
let activeStripeAccount = process.env.VUE_APP_SWISS_STRIPE_LIVE_PUBLISHABLE_KEY ? 'CH' : 'US';

export default {
          name: "Subscribe",
          mixins: [AuthMixin, SubscriptionMixin],
          props: {},
          data() {
            return {
              pageTitle: 'Sign Up',
              showSteps: true,
              error: null,
              cardError:null,
              isPurchasing: false,
              isValidatingPromo: false,
              promoValidationSequence: 0,
              step: 0,
              states: Constants.states,
              showPromotionCode: false,
              promotionCode: '',
              promotionCodeValidation: null,
              purchaseOptions: {
                paymentMethodId: null,
                promotionCodeId: null,
                stripeAccount: activeStripeAccount,
                priceName: 'StandardMonthly',
                firstName: '',
                lastName: '',
                line1: '',
                line2: '',
                city: '',
                state: '',
                postal_code: '',
              },
              steps: null,
              stepsOffset: 0
            }
          },
          computed: {
            subscriptionName: function () {
              return this.getSubscriptionName(this.purchaseOptions.priceName);
            },
            priceDuration: function () {
              return this.getPriceDuration(this.purchaseOptions.priceName);
            },
            subscriptionPrice: function () {
              return this.getSubscriptionPrice(this.purchaseOptions.priceName);
            },
            promotionCodeDiscount: function () {
              if (!this.promotionCodeValidation || !this.promotionCodeValidation.isValid)
                return 0;

              if (this.promotionCodeValidation.amountOff)
                return this.promotionCodeValidation.amountOff / 100;
              else if (this.promotionCodeValidation.percentOff)
                return this.subscriptionPrice * this.promotionCodeValidation.percentOff / 100;

              return null;
            },
            totalPrice: function () {
              return this.subscriptionPrice - this.promotionCodeDiscount;
            }
          },
          methods: {
            onAuthenticated: function () {
              this.step = 1;
            },
            onChangeStep: function (index) {
              this.step = index;
            },
            chooseSubscription: function (priceName) {
              this.purchaseOptions.priceName = priceName;
              this.step = 2;
            },
            changeStripeAccount: function (account) {
              if (this.isPurchasing) return;
              if (activeStripeAccount === account) return;
              const key = account === 'CH' ? process.env.VUE_APP_SWISS_STRIPE_LIVE_PUBLISHABLE_KEY : usStripeKey;
              if (!key || typeof Stripe === 'undefined') {
                this.showGeneralError('Stripe payment form is not available.');
                return;
              }
              if (cardElement) {
                cardElement.off('change', this.onCardChange);
                cardElement.destroy();
              }
              stripe = Stripe(key);
              elements = stripe.elements();
              cardElement = elements.create('card', { style: style });
              cardElement.mount(this.$refs.card);
              cardElement.on('change', this.onCardChange);
              activeStripeAccount = account;
              this.purchaseOptions.stripeAccount = account;
            },
            applyPromotionCode: function () {
              if (this.isPurchasing) return false;
              const validationSequence = ++this.promoValidationSequence;
              this.isValidatingPromo = !!this.promotionCode;
              this.promotionCodeValidation = null;
              if (!this.promotionCode) {
                this.promotionCodeValidation = null;
                this.showPromotionCode = false;
                this.changeStripeAccount('CH');
                return false;
              }

              axios
                .post('/api/subscription/validatePromotionCode', { code: this.promotionCode })
                .then(response => {
                  if (validationSequence !== this.promoValidationSequence) return;
                  this.isValidatingPromo = false;
                  console.log(response);

                  this.promotionCodeValidation = response.data;
                  this.showPromotionCode = false;
                  if (response.data.isValid && response.data.stripeAccount)
                    this.changeStripeAccount(response.data.stripeAccount);
                  else {
                    // Invalid/ambiguous promo: revert payment form to the default CH account.
                    // Keep the invalid validation result so the existing inline error is shown.
                    this.changeStripeAccount('CH');
                  }
                })
                .catch(error => {
                  if (validationSequence !== this.promoValidationSequence) return;
                  this.isValidatingPromo = false;
                  console.log(error);
                  // A failed lookup must never leave a stale US payment form active.
                  this.promotionCodeValidation = null;
                  this.changeStripeAccount('CH');
                  this.showGeneralError('We could not validate the promotion code. Please try again.');
                });

              return false;
            },
              onCardChange: function (event) {
                  // Stripe Element reports its own validation as the user types; show it under the field like TextControl does
                  this.cardError = event.error ? event.error.message : null;
              },
              showGeneralError: function (message) {
                  this.error = message;
                  // The general alert is at the top of a long form; bring it into view
                  this.$nextTick(() => {
                      if (this.$refs.errorAlert && this.$refs.errorAlert.$el)
                          this.$refs.errorAlert.$el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  });
              },
              handlePurchaseError: function (error) {
                  // ExceptionMiddleware returns { Message, Details } (PascalCase). A 400 (BadRequestException)
                  // carries the Stripe card message, e.g. "Your card was declined." -> show it on the card field.
                  const response = error && error.response;
                  const data = response && response.data;
                  const details = data && (data.Details || data.details);

                  if (response && response.status === 400 && details)
                      this.cardError = details;
                  else if (response && response.status === 409 && details)
                      // 409 = server-side duplicate protection (e.g. "You already have an active subscription.")
                      this.showGeneralError(details);
                  else
                      this.showGeneralError('We could not complete your purchase. Please contact support before trying again so we can check whether a subscription was created.');
              },
              onSubmit: function () {
                  if (this.isValidatingPromo) {
                      this.showGeneralError('Please wait for the promotion code to be validated.');
                      return;
                  }
                  if (this.isPurchasing)
                      return;

                  if (!stripe || !cardElement) {
                      this.showGeneralError('The payment form did not load. Please refresh the page and try again.');
                      return;
                  }

                  this.isPurchasing = true;
                  this.error = null;
                  this.cardError = null;

                  let purchaseSucceeded = false;

                  stripe.createPaymentMethod({
                      type: 'card',
                      card: cardElement
                  })
                      .then(result => {
                          if (result.error) {
                              // Bug fix: previously returned here without resetting isPurchasing -> spinner stuck forever
                              this.cardError = result.error.message;
                              return;
                          }

                          this.purchaseOptions.paymentMethodId = result.paymentMethod.id;

                          // Always set (or clear) the promo so a stale code is never sent
                          this.purchaseOptions.stripeAccount = activeStripeAccount;
                          this.purchaseOptions.promotionCodeId = (this.promotionCodeValidation && this.promotionCodeValidation.isValid)
                              ? this.promotionCodeValidation.id
                              : null;

                          return axios
                              .post('/api/subscription/purchase', this.purchaseOptions)
                              .then(() => {
                                  purchaseSucceeded = true;

                                  this.$parent.showToast('Your account has been created. Thank you!', {
                                      title: 'Saved',
                                      variant: 'success',
                                      autoHideDelay: 5000
                                  });

                                  // Refresh our auth data to get the new role
                                  return axios.post('/api/account/refresh', {});
                              })
                              .then(authResponse => {
                                  this.processAuthResponse(authResponse);
                                  this.$router.push('/');
                              });
                      })
                      .catch(error => {
                          console.log(error);

                          // Bug fix: server errors (e.g. declined card) were silent
                          if (purchaseSucceeded)
                              this.showGeneralError('Your subscription was created, but we could not refresh your session. Please log out and log back in.');
                          else
                              this.handlePurchaseError(error);
                      })
                      .then(() => {
                          // Always runs: success, card error, or failure -> spinner can never get stuck
                          this.isPurchasing = false;
                      });
            }
        },
          watch: {
          },
          created() {
          },
          async mounted () {
            if (this.isAuthenticated) {
              this.step = 1;
              this.stepsOffset = -1;
              this.pageTitle = 'Renew Subscription';
              this.steps = [
                {
                  label: 'Step 1<br />Choose a subscription',
                  icon: 'file-invoice-dollar'
                },
                {
                  label: 'Step 2<br />Provide payment details',
                  icon: 'credit-card'
                }
              ]
            } else {
              this.steps = [
                {
                  label: 'Step 1<br />Create an account',
                  icon: 'user-alt'
                },
                {
                  label: 'Step 2<br />Choose a subscription',
                  icon: 'file-invoice-dollar'
                },
                {
                  label: 'Step 3<br />Provide payment details',
                  icon: 'credit-card'
                }
              ]
            }
              cardElement.mount(this.$refs.card);
              cardElement.on('change', this.onCardChange);
        },
        beforeDestroy() {
            // cardElement is shared at module level; detach this instance's listener
            if (cardElement)
                cardElement.off('change', this.onCardChange);
        }
};
</script>